/**
 * @file Shared distinct-key walker for reference columns in a mapping review:
 * one pass over a column's override-applied rows, skipping user-rejected rows
 * and rows beyond the classified coverage (`rowLimit`, the server's import
 * cap — rows past it are never written). Accumulates each normalized key's
 * first display text and occurrence count. Single source of the counting
 * convention shared by the approval footer, the column cards, the resolution
 * Drawer, and the persisted summary (`recomputeSummary`), so the live UI and
 * the saved document can never disagree on what counts as a reference value.
 */
import type { Snapshot } from 'valtio';
import { tableReferenceExtractedKey } from '#types';
import type { TableChunk } from '#types';

export function referenceKeyOccurrences(args: {
  chunks: Snapshot<TableChunk[]>;
  sourceColumn: string;
  cellOverrides: Snapshot<Record<number, Record<string, string | number | null>>>;
  rejected: ReadonlySet<number>;
  rowLimit: number;
}): Map<string, { display: string; count: number }> {
  const { chunks, sourceColumn, cellOverrides, rejected, rowLimit } = args;
  const byKey = new Map<string, { display: string; count: number }>();
  chunks.reduce((offset, chunk) => {
    chunk.rows.forEach((row, i) => {
      const index = offset + i;
      if (index >= rowLimit || rejected.has(index)) return;
      const override = cellOverrides[index]?.[sourceColumn];
      const raw = override !== undefined ? override : row[sourceColumn];
      const key = tableReferenceExtractedKey(raw);
      if (!key) return;
      const entry = byKey.get(key);
      if (entry) entry.count += 1;
      else byKey.set(key, { display: String(raw).trim(), count: 1 });
    });
    return offset + chunk.rows.length;
  }, 0);
  return byKey;
}
